 function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import styled from '@emotion/styled'
import {Add as AddIcon, Remove as RemoveIcon, ThumbUp} from '@mui/icons-material'
import graphql from 'babel-plugin-relay/macro'
import React from 'react'
import {useFragment} from 'react-relay'
import useAtmosphere from '~/hooks/useAtmosphere'
import useMutationProps from '~/hooks/useMutationProps'

import VoteForReflectionGroupMutation from '../mutations/VoteForReflectionGroupMutation'
import {PALETTE} from '../styles/paletteV3'

import getGraphQLError from '../utils/relay/getGraphQLError'
import isTempId from '../utils/relay/isTempId'
import withMutationProps, {} from '../utils/relay/withMutationProps'


import FlatButton from './FlatButton'







const UpvoteRow = styled('div')({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'flex-end'
})

const ThumbUpIcon = styled(ThumbUp)({
  height: 18,
  width: 18
})

const StyledIcon = styled('div')({
  height: 24,
  width: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none'
})

const UpvoteButton = styled(FlatButton)(
  ({isExpanded, disabled}) => ({
    color: isExpanded ? '#fff' : PALETTE.SLATE_600,
    height: 24,
    lineHeight: '24px',
    padding: 0,
    width: 24,
    ':hover,:focus,:active': {
      backgroundColor: !disabled ? (isExpanded ? PALETTE.SLATE_500 : PALETTE.SLATE_200) : undefined,
      boxShadow: 'none'
    }
  })
)

const Votes = styled('span')(
  ({voteCount, isExpanded}) => ({
    color: isExpanded
      ? voteCount === 0
        ? PALETTE.SLATE_200
        : '#fff'
      : voteCount === 0
      ? PALETTE.SLATE_700
      : PALETTE.SKY_500,
    fontWeight: 600,
    padding: '0 4px',
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none'
  })
)

const UpvoteColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: 96
})

const makeHandleCompleted =
  (onCompleted, atmosphere) =>
  (res, errors) => {
    onCompleted()
    const error = getGraphQLError(res, errors)
    if (error) {
      atmosphere.eventEmitter.emit('addSnackbar', {
        key: 'voteError',
        message: error.message || 'Error submitting vote',
        autoDismiss: 5
      })
    }
  }

const ReflectionGroupVoting = (props) => {
  const {isExpanded, meeting: meetingRef, reflectionGroup: reflectionGroupRef} = props
  const meeting = useFragment(
    graphql`
      fragment ReflectionGroupVoting_meeting on RetrospectiveMeeting {
        localStage {
          isComplete
        }
        id
        viewerMeetingMember {
          votesRemaining
        }
        maxVotesPerGroup
      }
    `,
    meetingRef
  )
  const reflectionGroup = useFragment(
    graphql`
      fragment ReflectionGroupVoting_reflectionGroup on RetroReflectionGroup {
        id
        viewerVoteCount
      }
    `,
    reflectionGroupRef
  )
  const {id: reflectionGroupId} = reflectionGroup
  const {id: meetingId, localStage, maxVotesPerGroup, viewerMeetingMember} = meeting
  const {isComplete} = localStage
  const votesRemaining = _nullishCoalesce(_optionalChain([viewerMeetingMember, 'optionalAccess', _ => _.votesRemaining]), () => ( 0))
  const viewerVoteCount = Math.max(0, reflectionGroup.viewerVoteCount || 0)
  const canUpvote = viewerVoteCount < maxVotesPerGroup && votesRemaining > 0 && !isComplete
  const canDownvote = viewerVoteCount > 0 && !isComplete

  const atmosphere = useAtmosphere()
  const {onError, onCompleted, submitMutation} = useMutationProps()
  const vote = () => {
    if (isComplete || isTempId(reflectionGroupId) || !canUpvote) return
    submitMutation()
    const handleCompleted = makeHandleCompleted(onCompleted, atmosphere)
    VoteForReflectionGroupMutation(
      atmosphere,
      {reflectionGroupId},
      {onError, onCompleted: handleCompleted, meetingId}
    )
  }
  const downvote = () => {
    if (isComplete || isTempId(reflectionGroupId) || !canDownvote) return
    submitMutation()
    const handleCompleted = makeHandleCompleted(onCompleted, atmosphere)
    VoteForReflectionGroupMutation(
      atmosphere,
      {isUnvote: true, reflectionGroupId},
      {onError, onCompleted: handleCompleted, meetingId}
    )
  }

  return (
    React.createElement(UpvoteColumn, null
      , React.createElement(UpvoteRow, { 'data-cy': "reflection-vote-row",}
        , React.createElement(UpvoteButton, {
          'aria-label': `Remove vote`,
          isExpanded: isExpanded,
          disabled: !canDownvote,
          color: isExpanded ? PALETTE.SKY_400 : PALETTE.SKY_500,
          onClick: downvote,}

          , React.createElement(RemoveIcon, null )
        )
        , React.createElement(Votes, { isExpanded: isExpanded, voteCount: viewerVoteCount,}
          , React.createElement(StyledIcon, null
            , React.createElement(ThumbUpIcon, null )
          )
          , React.createElement('span', { 'data-cy': `completed-vote-count`,}, viewerVoteCount)
        )
        , React.createElement(UpvoteButton, {
          'aria-label': `Add vote`,
          isExpanded: isExpanded,
          disabled: !canUpvote,
          color: isExpanded ? 'rgba(255, 255, 255, .65)' : PALETTE.SLATE_600,
          onClick: vote,}

          , React.createElement(AddIcon, null )
        )
      )
    )
  )
}

export default withMutationProps(ReflectionGroupVoting)
